import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import useMobile from '../../hooks/useMobile';

const C = {
  blue: '#6699ff',
  blueDark: '#4d7ae6',
  navy: '#0b1120',
  navyMid: '#162040',
  cream: '#f5f0e8',
  creamLight: '#faf7f2',
  creamWarm: '#ede5d8',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#536179',
  white: '#ffffff',
  divider: 'rgba(102,153,255,0.12)',
};

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTag, setActiveTag] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const isMobile = useMobile();

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    const { data } = await supabase
      .from('articles')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false });

    if (data) setArticles(data);
    setLoading(false);
  };

  const categories = ['all', ...new Set(articles.map(a => a.category).filter(Boolean))];
  const allTags = [...new Set(articles.flatMap(a => a.tags || []).filter(Boolean))].sort();

  const filtered = articles.filter(a => {
    if (activeCategory !== 'all' && a.category !== activeCategory) return false;
    if (activeTag && !(a.tags || []).includes(activeTag)) return false;
    if (search) {
      const s = search.toLowerCase();
      const matchTitle = a.title.toLowerCase().includes(s);
      const matchExcerpt = (a.excerpt || '').toLowerCase().includes(s);
      const matchTags = (a.tags || []).some(t => t.toLowerCase().includes(s));
      if (!matchTitle && !matchExcerpt && !matchTags) return false;
    }
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: C.creamLight, fontFamily: "'Manrope', sans-serif" }}>
      {/* Nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: isMobile ? '0 16px' : '0 60px', height: isMobile ? 56 : 72,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(245,240,232,0.95)', backdropFilter: 'blur(24px)',
        borderBottom: `1px solid ${C.creamWarm}`,
      }}>
        <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: isMobile ? 11 : 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.navy }}>
            SPC <span style={{ color: C.blue }}>Insights</span>
          </span>
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 16 : 32 }}>
          <a href="/" style={{ fontSize: isMobile ? 11 : 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: C.textBody, textDecoration: 'none' }}>Home</a>
          <a href="/articles" style={{ fontSize: isMobile ? 11 : 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: C.blue, textDecoration: 'none' }}>Articles</a>
          {!isMobile && <a href="/publications" style={{ fontSize: 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: C.textBody, textDecoration: 'none' }}>Publications</a>}
        </div>
      </nav>

      {/* Hero */}
      <div style={{
        background: C.navy, padding: isMobile ? '100px 24px 48px' : '140px 80px 80px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(102,153,255,0.06) 0%, transparent 60%)',
          top: -200, right: -100,
        }} />
        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{
            fontSize: isMobile ? 9 : 10, fontWeight: 700, letterSpacing: isMobile ? 3 : 4, textTransform: 'uppercase',
            color: C.blue, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
            animation: 'fadeInLeft 0.8s ease 0.1s both',
          }}>
            <span style={{ height: 1, background: C.blue, display: 'inline-block', width: 32 }} />
            Insights & Knowledge
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? 36 : 56, fontWeight: 400,
            color: C.cream, lineHeight: 1.1, marginBottom: 16,
            animation: 'fadeInLeft 0.9s ease 0.3s both',
          }}>
            Industry <em style={{ fontStyle: 'italic', color: C.creamWarm }}>Insights</em>
          </h1>
          <p style={{
            fontSize: isMobile ? 14 : 16, lineHeight: 1.7, color: 'rgba(245,240,232,0.5)', maxWidth: 540,
            animation: 'fadeInUp 0.8s ease 0.6s both',
          }}>
            Expert perspectives on steel processing, operational excellence, and the evolving landscape of the global metals industry.
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '24px 16px 0' : '32px 80px 0' }}>
        {/* Search */}
        <div style={{ marginBottom: 20 }}>
          <label htmlFor="search-articles" className="sr-only">Search articles</label>
          <input
            id="search-articles"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles..."
            style={{
              width: '100%', maxWidth: isMobile ? '100%' : 400, padding: '12px 16px',
              border: `1px solid ${C.creamWarm}`, background: C.white,
              fontFamily: "'Manrope', sans-serif", fontSize: 16,
              color: C.textDark, outline: 'none', transition: 'all 0.3s ease',
            }}
            onFocus={e => { e.target.style.borderColor = C.blue; e.target.style.boxShadow = '0 0 0 3px rgba(102,153,255,0.08)'; }}
            onBlur={e => { e.target.style.borderColor = C.creamWarm; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        {/* Categories */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: isMobile ? '6px 14px' : '8px 20px',
                border: `1px solid ${activeCategory === cat ? C.blue : C.creamWarm}`,
                background: activeCategory === cat ? 'rgba(102,153,255,0.08)' : C.white,
                color: activeCategory === cat ? C.blue : C.textBody,
                fontFamily: "'Manrope', sans-serif", fontSize: isMobile ? 10 : 11, fontWeight: 600,
                letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {cat === 'all' ? `All (${articles.length})` : cat}
            </button>
          ))}
        </div>

        {/* Tags */}
        {allTags.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setActiveTag(activeTag === tag ? '' : tag)}
                style={{
                  padding: '5px 14px',
                  border: `1px solid ${activeTag === tag ? C.blue : 'rgba(102,153,255,0.12)'}`,
                  background: activeTag === tag ? 'rgba(102,153,255,0.1)' : 'rgba(102,153,255,0.03)',
                  color: activeTag === tag ? C.blue : C.textMuted,
                  fontFamily: "'Manrope', sans-serif", fontSize: 10, fontWeight: 600,
                  letterSpacing: 0.5, cursor: 'pointer', transition: 'all 0.2s ease',
                }}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Results count */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.creamWarm}`, fontSize: 12, color: C.textMuted }}>
          {filtered.length} {filtered.length === 1 ? 'article' : 'articles'} found
          {(activeCategory !== 'all' || activeTag || search) && (
            <button
              onClick={() => { setActiveCategory('all'); setActiveTag(''); setSearch(''); }}
              style={{
                marginLeft: 12, background: 'none', border: 'none',
                color: C.blue, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Articles Grid */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '24px 16px 60px' : '32px 80px 100px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: C.textMuted }}>Loading articles...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ fontSize: 18, color: C.textMuted, marginBottom: 8 }}>
              {articles.length === 0 ? 'No articles published yet' : 'No articles match your filters'}
            </div>
            <p style={{ fontSize: 14, color: C.textMuted }}>
              {articles.length === 0 ? 'Check back soon for expert insights from our team.' : 'Try adjusting your search or filters.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 16 : 24 }}>
            {filtered.map((article, index) => (
              <ArticleCard key={article.id} article={article} index={index} onClick={() => navigate(`/articles/${article.slug}`)} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        background: C.navy, padding: isMobile ? '32px 16px' : '40px 80px',
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'center' : 'center',
        gap: isMobile ? 8 : 0,
      }}>
        <p style={{ fontSize: 12, color: 'rgba(245,240,232,0.25)' }}>© 2026 Steel Process Consultancy Inc.</p>
        <a href="/" style={{ fontSize: 12, color: 'rgba(245,240,232,0.4)', textDecoration: 'none' }}>Back to main site</a>
      </div>
    </div>
  );
}

function ArticleCard({ article, onClick, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: C.white, border: `1px solid ${C.creamWarm}`,
        cursor: 'pointer', transition: 'all 0.4s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered ? '0 16px 48px rgba(10,20,40,0.08)' : 'none',
        overflow: 'hidden',
        animation: `fadeInUp 0.6s ease ${0.2 + (index * 0.1)}s both`,
      }}
    >
      {article.cover_image ? (
        <div style={{ width: '100%', height: 200, overflow: 'hidden' }}>
          <img src={article.cover_image} alt={article.title} style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transition: 'transform 0.4s ease',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
          }} />
        </div>
      ) : (
        <div style={{
          width: '100%', height: 200,
          background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 48, color: 'rgba(102,153,255,0.1)' }}>SPC</span>
        </div>
      )}

      <div style={{ padding: '24px 24px 28px' }}>
        {article.category && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
            color: C.blue, display: 'block', marginBottom: 10,
          }}>
            {article.category}
          </span>
        )}
        <h3 style={{
          fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600,
          color: hovered ? C.blue : C.textDark, lineHeight: 1.3, marginBottom: 10,
          transition: 'color 0.3s ease',
        }}>
          {article.title}
        </h3>
        {article.excerpt && (
          <p style={{ fontSize: 13, lineHeight: 1.65, color: C.textBody, marginBottom: 16 }}>
            {article.excerpt.length > 120 ? article.excerpt.slice(0, 120) + '...' : article.excerpt}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, color: C.textMuted }}>
            {new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <span style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
            color: hovered ? C.blue : C.textMuted, transition: 'color 0.3s ease',
          }}>
            Read →
          </span>
        </div>
      </div>
    </div>
  );
}